<template>
  <div class="h-full flex flex-col min-h-0 bg-[#fafbfc] relative overflow-hidden min-w-0">
    <header class="px-8 py-6 sticky top-0 z-30 bg-[#fafbfc]/90 backdrop-blur-md border-b border-[#1c4848]/5 shrink-0">
      <div class="max-w-[1600px] mx-auto flex flex-wrap items-center justify-between gap-4">
        <div>
          <div class="flex items-center gap-2 text-[#1c4848]/40 mb-1">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="shrink-0" aria-hidden="true"><path d="M12 19V5"/><path d="m5 12 7-7 7 7"/></svg>
            <span class="text-[10px] font-bold uppercase tracking-wider">Dashboard</span>
          </div>
          <h1 class="text-2xl font-serif font-medium text-[#1c4848] tracking-tight">Feed Management</h1>
        </div>
        <RouterLink
          :to="{ name: 'feed-add' }"
          class="flex items-center gap-2 px-5 py-2.5 bg-[#1c4848] text-white rounded-xl shadow-lg shadow-[#1c4848]/20 hover:bg-[#153838] active:scale-95 transition-all"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.500" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>
          <span class="text-xs font-bold tracking-wide">Add to feed</span>
        </RouterLink>
      </div>
    </header>

    <main class="flex-1 min-h-0 overflow-y-auto custom-scrollbar px-4 sm:px-8 pt-6 pb-32">
      <div class="max-w-[1600px] mx-auto space-y-5">
        <div class="grid grid-cols-2 gap-3 sm:grid-cols-5" role="group" aria-label="Which feed to show">
          <button
            v-for="l in languageTiles"
            :key="l.id"
            type="button"
            class="rounded-2xl px-4 py-3 text-left transition-all"
            :class="languageId === l.id ? 'bg-[#1c4848] text-white shadow-md' : 'bg-white border border-[#1c4848]/10 text-[#1c4848] hover:bg-[#fafbfc]'"
            :aria-pressed="languageId === l.id"
            @click="languageId = l.id"
          >
            <span class="block font-serif text-base font-bold">{{ l.name }}</span>
            <span class="mt-0.5 block text-[11px] font-semibold" :class="languageId === l.id ? 'text-white/70' : l.total ? 'text-[#1c4848]/50' : 'text-[#a0833c]'">{{ l.status }}</span>
          </button>
        </div>

        <div class="flex flex-wrap items-center justify-between gap-3">
          <div class="flex gap-2" role="group" aria-label="Part of the feed">
            <button
              v-for="s in SECTIONS"
              :key="s.value"
              type="button"
              class="px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-colors"
              :class="section === s.value ? 'bg-[#1c4848] text-white shadow-md' : 'bg-white border border-[#1c4848]/10 text-[#1c4848]/60 hover:bg-[#fafbfc] hover:text-[#1c4848]'"
              :aria-pressed="section === s.value"
              @click="section = s.value"
            >
              {{ s.label }}<span class="ml-1.5 font-semibold tabular-nums opacity-60">{{ countOf(s.value) }}</span>
            </button>
          </div>
          <p class="text-xs font-semibold text-[#1c4848]/50">{{ feedNote }}</p>
        </div>

        <div class="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div class="min-w-0">
            <div v-if="contentFeedStore.contentFeedsLoading && !feedList.length" class="space-y-3" aria-label="Loading the feed">
              <div v-for="n in 5" :key="n" class="h-28 rounded-[24px] bg-[#1c4848]/5 animate-pulse"></div>
            </div>

            <div v-else-if="loadError" role="alert" class="rounded-[24px] border border-[#1c4848]/10 bg-white p-12 text-center">
              <h3 class="font-serif text-lg font-semibold text-[#1c4848] mb-3">The feed could not be loaded</h3>
              <button type="button" class="min-h-11 px-5 rounded-xl text-xs font-bold text-white bg-[#1c4848]" @click="load">Try again</button>
            </div>

            <div v-else-if="!feedList.length" class="rounded-[24px] border border-[#1c4848]/10 bg-white p-12 text-center">
              <h3 class="font-serif text-lg font-semibold text-[#1c4848] mb-1">No items in this feed</h3>
              <p class="text-sm text-[#1c4848]/50">{{ emptyNote }}</p>
              <RouterLink :to="{ name: 'feed-add' }" class="mt-5 inline-flex min-h-11 items-center px-5 rounded-xl text-xs font-bold text-white bg-[#1c4848] hover:bg-[#153838]">Find something to post</RouterLink>
            </div>

            <template v-else>
              <Draggable v-model="feedList" handle=".drag-handle" item-key="id" class="space-y-3" @end="onReorderEnd">
                <template #item="{ element: item }">
                  <FeedRow :item="item" @remove="removeItem(item)" />
                </template>
              </Draggable>
              <p v-if="saveError" role="alert" class="mt-3 text-sm font-medium text-red-700">{{ saveError }}</p>
            </template>
          </div>

          <aside class="lg:sticky lg:top-0" aria-label="How the feed looks on a phone">
            <FeedPhoneFrame :title="section === 'kids' ? 'Kids' : 'Deenly'">
              <FeedPhoneCard v-for="item in phoneItems" :key="item.id" :post="phonePostFromFeed(item)" :language-id="languageId" />
              <p v-if="!phoneItems.length" class="pt-24 text-center text-sm text-[#1c4848]/50">The {{ languageName }} {{ section === 'kids' ? 'Kids section' : 'feed' }} is empty on the phone.</p>
            </FeedPhoneFrame>
          </aside>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import Draggable from 'vuedraggable';
import { useContentFeedStore } from '@/store/content-feed';
import { KIDS_FEED_TYPES, TYPES_NOT_IN_APP, phonePostFromFeed } from '@/utils/contentFeedType.js';
import FeedRow from '@/components/app/dashboard/feed/FeedRow.vue';
import FeedPhoneCard from '@/components/app/dashboard/feed/FeedPhoneCard.vue';
import FeedPhoneFrame from '@/components/app/dashboard/feed/FeedPhoneFrame.vue';

const LANGUAGES = [
  { id: 2, name: 'Bosnian' },
  { id: 1, name: 'English' },
  { id: 3, name: 'German' },
  { id: 4, name: 'Dutch' },
  { id: 5, name: 'French' },
];
const SECTIONS = [
  { value: 'main', label: 'Home feed' },
  { value: 'kids', label: 'Kids' },
];

const contentFeedStore = useContentFeedStore();
const languageId = ref(2);
const section = ref('main');
const feedList = ref([]);
const insights = ref(null);
const loadError = ref(false);
const saveError = ref('');

const languageName = computed(() => LANGUAGES.find((l) => l.id === languageId.value)?.name ?? '');
const currentState = computed(() => insights.value?.languages?.find((l) => l.id === languageId.value));

const languageTiles = computed(() => LANGUAGES.map((l) => {
  const state = insights.value?.languages?.find((s) => s.id === l.id);
  return { ...l, total: state?.total ?? 0, status: state ? postsLabel(state.total) : ' ' };
}));

const feedNote = computed(() => {
  const state = currentState.value;
  if (!state) return '';
  const readers = state.devices ? `${state.devices} phones read this feed.` : 'No phone uses this language yet.';
  if (state.days_since === null) return readers;
  if (state.days_since === 0) return `Last post today. ${readers}`;
  return `Last post ${state.days_since} ${state.days_since === 1 ? 'day' : 'days'} ago. ${readers}`;
});

const emptyNote = computed(() => (section.value === 'kids'
  ? `The ${languageName.value} Kids section has no books, games or nasheeds on the feed.`
  : `The ${languageName.value} home feed has no posts yet.`));

const phoneItems = computed(() => feedList.value.filter((f) => !TYPES_NOT_IN_APP.includes(f.type)));

function postsLabel(total) {
  if (!total) return 'No posts yet';
  return `${total} ${total === 1 ? 'post' : 'posts'}`;
}

function inSection(feed, value) {
  return KIDS_FEED_TYPES.includes(feed.type) === (value === 'kids');
}

function countOf(value) {
  return (contentFeedStore.contentFeeds ?? []).filter((f) => inSection(f, value)).length;
}

async function load() {
  loadError.value = false;
  try {
    await contentFeedStore.fetchContentFeeds({ per_page: 100, language_id: languageId.value });
  } catch {
    loadError.value = true;
  }
}

async function loadInsights() {
  try {
    insights.value = await contentFeedStore.fetchStudioInsights(languageId.value);
  } catch {
    insights.value = null;
  }
}

async function onReorderEnd() {
  saveError.value = '';
  try {
    await contentFeedStore.reorderSection(feedList.value.map((f) => f.id));
  } catch {
    saveError.value = 'The new order could not be saved. Reload and try again.';
  }
}

async function removeItem(item) {
  saveError.value = '';
  try {
    await contentFeedStore.deleteContentFeed(item.id);
    loadInsights();
  } catch {
    saveError.value = 'This post could not be removed. Try again.';
  }
}

watch([() => contentFeedStore.contentFeeds, section], () => {
  feedList.value = (contentFeedStore.contentFeeds ?? []).filter((f) => inSection(f, section.value));
}, { immediate: true });

watch(languageId, () => {
  load();
  loadInsights();
});

onMounted(() => {
  load();
  loadInsights();
});
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar { width: 4px; height: 4px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background-color: rgba(28, 72, 72, 0.1); border-radius: 20px; }
.custom-scrollbar:hover::-webkit-scrollbar-thumb { background-color: rgba(28, 72, 72, 0.2); }
</style>
